import React, { useEffect, useState } from 'react'
import { TFunction } from 'i18next'
import { Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { BrowserMultiFormatReader, Result } from '@zxing/library'

import { AppError } from '../common/AppError'
import { isScreenSizeSmall } from '../common/utils'

import QRCodeJsonUploadCard from './QRCodeJsonUploadCard'
import QRCodeScanCard from './QRCodeScanCard'
import QRCodeScannerCameraFeed from './QRCodeScannerCameraFeed'

import '../../css/unlockPin/QRCodeUpload.css'

type QRCodeUploadProps = {
  disable: boolean
  handleSubmit: (data: string) => void
}

const validImageFileTypes: string[] = ['image/png', 'image/jpeg', 'image/jpg']

export const readFileAsDataURL = (
  file: File,
  t: TFunction<'translation', undefined>,
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (error) =>
      reject(
        new AppError({
          name: 'FileReaderError',
          message: t('unlock_pin_page.read_error'),
          innerError: error,
          errorCode: 0,
        }),
      )
  })
}

export const createImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve) => {
    const img = new Image()
    img.src = src
    img.onload = () => resolve(img)
  })
}

/**
 * QRCodeUpload component.
 *
 * This component allows users to scan QR codes either using their device's camera or by uploading an image file.
 *
 * @param {QRCodeUploadProps} props - The properties for the component.
 * @returns {JSX.Element} The QRCodeUpload component.
 */
const QRCodeUpload = ({
  disable,
  handleSubmit,
}: QRCodeUploadProps): JSX.Element => {
  const [qrCodeReader, setQRCodeReader] = useState<
    BrowserMultiFormatReader | undefined
  >(undefined)
  const [isScanning, setIsScanning] = useState<boolean>(false)
  const { t } = useTranslation()
  useEffect(() => {
    const codeReaderInstance = new BrowserMultiFormatReader()
    setQRCodeReader(codeReaderInstance)

    return () => {
      codeReaderInstance.reset()
    }
  }, [])

  const isEmptyObject = (obj: unknown): boolean => {
    return obj === null || obj === undefined || obj === ''
  }

  const handleScanFromVideoDevice = (): void => {
    if (qrCodeReader) {
      setIsScanning(true)
      const constraints = { video: { facingMode: 'environment' } }
      qrCodeReader
        .decodeFromConstraints(constraints, 'video', (result: Result) => {
          if (result) {
            const data = result.getText()
            stopScanningFromVideoDevice()
            if (!isEmptyObject(data)) {
              handleSubmit(data)
            }
          }
        })
        .catch((err) => {
          stopScanningFromVideoDevice()
          toast.error(t('unlock_pin_page.scan_failed') + `: ${err.message}`)
        })
    }
  }

  const handleJsonPayloadUpload = (jsonPayload: string): void => {
    handleSubmit(jsonPayload)
  }

  const stopScanningFromVideoDevice = (): void => {
    if (qrCodeReader) {
      qrCodeReader.reset()
      setIsScanning(false)
    }
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
  ): void => {
    if (event.key === 'Enter') {
      event.preventDefault()
    }
  }

  return (
    <>
      {!isScanning && (
        <Row>
          <Col xs={12} lg={6} className="mt-3">
            <QRCodeScanCard
              disable={disable}
              handleScanFromVideoDevice={handleScanFromVideoDevice}
            />
          </Col>
          {!isScreenSizeSmall() && (
            <Col xs={12} lg={6} className="mt-3">
              <QRCodeJsonUploadCard
                disable={disable}
                handleJsonPayloadUpload={handleJsonPayloadUpload}
              />
            </Col>
          )}
        </Row>
      )}

      <QRCodeScannerCameraFeed
        isScanning={isScanning}
        disable={disable}
        stopScanningFromVideoDevice={stopScanningFromVideoDevice}
      />
    </>
  )
}

export default QRCodeUpload
